import { Injectable } from '@angular/core';

export interface StorageItem<T> {
  value: T;
  timestamp: number;
  expiresIn?: number; // milliseconds
}

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private readonly DEFAULT_EXPIRY = 30 * 24 * 60 * 60 * 1000; // 30 days

  /**
   * Set item in sessionStorage with optional expiration
   */
  setItem<T>(key: string, value: T, expiresIn?: number): void {
    const item: StorageItem<T> = {
      value,
      timestamp: Date.now(),
      expiresIn: expiresIn || this.DEFAULT_EXPIRY,
    };

    try {
      sessionStorage.setItem(key, JSON.stringify(item));
    } catch (error) {
      console.error('Failed to set sessionStorage item:', error);
    }
  }

  /**
   * Get item from sessionStorage with expiration check
   */
  getItem<T>(key: string, defaultValue?: T): T | null {
    try {
      const itemStr = sessionStorage.getItem(key);
      if (!itemStr) {
        return defaultValue || null;
      }

      const item: StorageItem<T> = JSON.parse(itemStr);

      // Check if item has expired
      if (item.expiresIn && Date.now() - item.timestamp > item.expiresIn) {
        this.removeItem(key);
        return defaultValue || null;
      }

      return item.value;
    } catch (error) {
      console.error('Failed to get sessionStorage item:', error);
      return defaultValue || null;
    }
  }

  /**
   * Remove item from sessionStorage
   */
  removeItem(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.error('Failed to remove sessionStorage item:', error);
    }
  }

  /**
   * Clear all expired items from sessionStorage
   */
  clearExpiredItems(): void {
    try {
      const keysToRemove: string[] = [];

      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (!key) continue;

        const itemStr = sessionStorage.getItem(key);
        if (!itemStr) continue;

        try {
          const item: StorageItem<any> = JSON.parse(itemStr);
          if (item.expiresIn && Date.now() - item.timestamp > item.expiresIn) {
            keysToRemove.push(key);
          }
        } catch {
          // Invalid format, remove it
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => sessionStorage.removeItem(key));

      if (keysToRemove.length > 0) {
        console.log(
          `🧹 Cleared ${keysToRemove.length} expired sessionStorage items`
        );
      }
    } catch (error) {
      console.error('Failed to clear expired items:', error);
    }
  }

  /**
   * Check if sessionStorage is available
   */
  isStorageAvailable(): boolean {
    try {
      const testKey = '__storage_test__';
      sessionStorage.setItem(testKey, 'test');
      sessionStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get storage usage information
   */
  getStorageInfo(): { used: number; available: number; percentage: number } {
    try {
      let used = 0;
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key) {
          used += key.length + (sessionStorage.getItem(key)?.length || 0);
        }
      }

      // Most browsers have ~5MB limit for sessionStorage
      const available = 5 * 1024 * 1024; // 5MB in bytes
      const percentage = (used / available) * 100;

      return { used, available, percentage };
    } catch {
      return { used: 0, available: 0, percentage: 0 };
    }
  }

  /**
   * Clear only translation-related items from sessionStorage
   */
  clearTranslationItems(): void {
    try {
      const translationKeys = [
        'user_language',
        'language_change_timestamp',
        'translation_cache',
        'last_translation_load',
        'selected_language',
        'translation_performance',
        'translation_debug',
        'cached_translations',
        'translation_version',
      ];

      const keysToRemove: string[] = [];

      // Check for exact matches
      translationKeys.forEach((key) => {
        if (sessionStorage.getItem(key) !== null) {
          keysToRemove.push(key);
        }
      });

      // Check for keys that start with translation-related prefixes
      const translationPrefixes = ['translation_', 'lang_', 'locale_', 'i18n_'];

      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (
          key &&
          translationPrefixes.some((prefix) => key.startsWith(prefix))
        ) {
          if (!keysToRemove.includes(key)) {
            keysToRemove.push(key);
          }
        }
      }

      // Remove all translation-related keys
      keysToRemove.forEach((key) => {
        sessionStorage.removeItem(key);
      });

      if (keysToRemove.length > 0) {
        console.log(
          `🌍 Cleared ${keysToRemove.length} translation-related sessionStorage items:`,
          keysToRemove
        );
      } else {
        console.log(
          '🌍 No translation-related sessionStorage items found to clear'
        );
      }

      return;
    } catch (error) {
      console.error('Failed to clear translation items:', error);
    }
  }

  /**
   * Get all translation-related keys in sessionStorage
   */
  getTranslationKeys(): string[] {
    try {
      const translationKeys = [
        'user_language',
        'language_change_timestamp',
        'translation_cache',
        'last_translation_load',
        'selected_language',
        'translation_performance',
        'translation_debug',
        'cached_translations',
        'translation_version',
      ];

      const foundKeys: string[] = [];
      const translationPrefixes = ['translation_', 'lang_', 'locale_', 'i18n_'];

      // Check exact matches
      translationKeys.forEach((key) => {
        if (sessionStorage.getItem(key) !== null) {
          foundKeys.push(key);
        }
      });

      // Check prefixed keys
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (
          key &&
          translationPrefixes.some((prefix) => key.startsWith(prefix))
        ) {
          if (!foundKeys.includes(key)) {
            foundKeys.push(key);
          }
        }
      }

      return foundKeys;
    } catch (error) {
      console.error('Failed to get translation keys:', error);
      return [];
    }
  }
}
